import React, { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Compass, Sun, Cloud, Flame, Tent, Users, Sparkles, Check, ArrowRight, Calendar } from "lucide-react";
import { api } from "../lib/api";

/**
 * /share/reels-kit — six themed 9:16 reel scenes for Instagram, TikTok and FB.
 *
 * Workflow:
 *   1. Open this page on your phone in landscape-locked vertical (or your desktop browser in
 *      portrait 1080×1920) — each scene fills the whole viewport one after another.
 *   2. Either SCREENSHOT each scene, or SCREEN-RECORD scrolling through them.
 *   3. Stitch the shots into a Reel in CapCut / Instagram / TikTok with a soundtrack.
 *
 * Each scene is a self-contained vertical card with:
 *   • Bold hook headline (the thumb-stop)
 *   • Supporting line (the curiosity hook)
 *   • Live brand stat (changes as the DB grows — never goes stale)
 *   • QR code to camp-compass.uk + handle + CTA
 *
 * The script panel below shows captions + hashtags + best posting times so
 * the marketing copy is ready to paste straight into the platform.
 */

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");

// Real, royalty-free Pexels CDN photos (Pexels license = free for commercial
// use, no attribution required). Curated to UK / outdoor / "blue sky" vibe.
// Each scene falls back to its own gradient if a photo fails to load.
const PHOTOS = {
  bank_holiday: "https://images.pexels.com/photos/1687845/pexels-photo-1687845.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop",
  scorcher:     "https://images.pexels.com/photos/2422915/pexels-photo-2422915.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop",
  many_sites:   "https://images.pexels.com/photos/2422265/pexels-photo-2422265.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop",
  speed:        "https://images.pexels.com/photos/2422588/pexels-photo-2422588.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop",
  community:    "https://images.pexels.com/photos/2398220/pexels-photo-2398220.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop",
  signup:       "https://images.pexels.com/photos/2581922/pexels-photo-2581922.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop",
};

/** Single 9:16 scene wrapper — fills the viewport vertically. */
function Scene({ bg, gradient, children, index }) {
  return (
    <section
      className="relative w-full mx-auto flex flex-col justify-between text-white overflow-hidden"
      style={{
        aspectRatio: "9 / 16",
        maxWidth: 540,
        background: gradient,
        backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.15) 30%, rgba(0,0,0,0.65) 100%), url("${bg}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      data-testid={`reel-scene-${index}`}
    >
      {children}
    </section>
  );
}

function StickerBadge({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 bg-[#ff4500] text-white text-[11px] font-bold uppercase tracking-[0.2em] ${className}`}>
      {children}
    </span>
  );
}

export default function ReelsKit() {
  const [count, setCount] = useState(11060);
  useEffect(() => {
    let alive = true;
    api.get("/campsites/stats").then(({ data }) => {
      if (alive && data?.total) setCount(data.total);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const fmt = useMemo(() => count.toLocaleString("en-GB"), [count]);

  return (
    <div className="bg-[#0e0e0e] min-h-screen text-white" data-testid="reels-kit-page">
      <header className="max-w-3xl mx-auto px-5 py-8">
        <div className="eyebrow text-[#ff4500] mb-2">Reels Kit · Bank Holiday Launch</div>
        <h1 className="font-display text-3xl sm:text-4xl mb-3">Six Reels, ready to post.</h1>
        <p className="text-sm text-[#bbb] leading-relaxed max-w-xl">
          Screenshot or screen-record each card below, drop them into your Reels editor (Instagram, TikTok, CapCut, FB) with a punchy sound, and post. Captions + hashtags + best post times are at the bottom — copy-paste ready.
        </p>
      </header>

      {/* SCENES — each one is a standalone 9:16 reel-card */}
      <div className="flex flex-col items-center gap-12 px-3 pb-12">

        {/* 1 — BANK HOLIDAY SCORCHER (hero) */}
        <Scene index="1" bg={PHOTOS.bank_holiday} gradient="linear-gradient(180deg,#ff7e3b 0%,#ff4500 100%)">
          <div className="p-6 sm:p-8 pt-12">
            <StickerBadge><Sun className="w-3.5 h-3.5" /> Bank Holiday Alert</StickerBadge>
          </div>
          <div className="px-6 sm:px-8 text-center">
            <div className="font-display text-[44px] sm:text-[64px] leading-[0.95] mb-4 drop-shadow-lg">
              EVERY<br/>CAMPSITE<br/>IS BOOKED.
            </div>
            <div className="text-base sm:text-xl font-bold leading-snug text-[#ffe7d6] drop-shadow">
              Except for the {fmt} we just found.
            </div>
          </div>
          <div className="p-6 sm:p-8 flex items-end justify-between gap-4">
            <div>
              <div className="text-[10px] mono uppercase tracking-[0.25em] text-white/80 mb-1">Tap → search free now</div>
              <div className="font-display text-lg">camp-compass.uk</div>
            </div>
            <div className="bg-white p-2 shrink-0">
              <QRCodeSVG value={PUBLIC_URL} size={84} bgColor="#ffffff" fgColor="#111111" includeMargin={false} />
            </div>
          </div>
        </Scene>

        {/* 2 — IT'S SCORCHING OUTSIDE */}
        <Scene index="2" bg={PHOTOS.scorcher} gradient="linear-gradient(180deg,#ffb449 0%,#ff7e3b 100%)">
          <div className="p-6 sm:p-8 pt-12">
            <StickerBadge><Flame className="w-3.5 h-3.5" /> 28°C this weekend</StickerBadge>
          </div>
          <div className="px-6 sm:px-8 text-center">
            <div className="font-display text-[40px] sm:text-[58px] leading-[0.95] mb-4 drop-shadow-lg">
              Don't<br/>waste it<br/>indoors.
            </div>
            <div className="text-base sm:text-xl font-bold leading-snug text-[#fff3e0]">
              The forecast says sun. The map says go.
            </div>
          </div>
          <div className="p-6 sm:p-8 flex items-end justify-between gap-4">
            <div>
              <div className="text-[10px] mono uppercase tracking-[0.25em] text-white/80 mb-1">Find a pitch</div>
              <div className="font-display text-lg">camp-compass.uk</div>
            </div>
            <div className="bg-white p-2 shrink-0">
              <QRCodeSVG value={PUBLIC_URL} size={84} bgColor="#ffffff" fgColor="#111111" includeMargin={false} />
            </div>
          </div>
        </Scene>

        {/* 3 — 11,000+ CAMPSITES (volume) */}
        <Scene index="3" bg={PHOTOS.many_sites} gradient="linear-gradient(180deg,#1e6f3b 0%,#0b3d1f 100%)">
          <div className="p-6 sm:p-8 pt-12">
            <StickerBadge><Tent className="w-3.5 h-3.5" /> Every UK county covered</StickerBadge>
          </div>
          <div className="px-6 sm:px-8 text-center">
            <div className="font-display text-[80px] sm:text-[120px] leading-none mb-1 drop-shadow-lg">
              {fmt}
            </div>
            <div className="font-display text-2xl sm:text-3xl mb-3">UK campsites.</div>
            <div className="text-base sm:text-xl font-bold leading-snug text-white/85">
              One map. Filter by price, dogs, hot showers, the lot.
            </div>
          </div>
          <div className="p-6 sm:p-8 flex items-end justify-between gap-4">
            <div>
              <div className="text-[10px] mono uppercase tracking-[0.25em] text-white/80 mb-1">Open the map free</div>
              <div className="font-display text-lg">camp-compass.uk</div>
            </div>
            <div className="bg-white p-2 shrink-0">
              <QRCodeSVG value={PUBLIC_URL} size={84} bgColor="#ffffff" fgColor="#111111" includeMargin={false} />
            </div>
          </div>
        </Scene>

        {/* 4 — 60 SECONDS (speed) */}
        <Scene index="4" bg={PHOTOS.speed} gradient="linear-gradient(180deg,#2563eb 0%,#0c2557 100%)">
          <div className="p-6 sm:p-8 pt-12">
            <StickerBadge><Sparkles className="w-3.5 h-3.5" /> 60-second search</StickerBadge>
          </div>
          <div className="px-6 sm:px-8 text-center">
            <div className="font-display text-[40px] sm:text-[56px] leading-[0.95] mb-4 drop-shadow-lg">
              Tired of<br/>checking<br/>30 tabs?
            </div>
            <div className="text-base sm:text-xl font-bold leading-snug text-white/90 mb-3">
              We did the boring bit.
            </div>
            <div className="text-sm sm:text-base text-white/75 leading-relaxed max-w-[28ch] mx-auto">
              Compare prices, availability and reviews across {fmt} UK campsites — in one tap.
            </div>
          </div>
          <div className="p-6 sm:p-8 flex items-end justify-between gap-4">
            <div>
              <div className="text-[10px] mono uppercase tracking-[0.25em] text-white/80 mb-1">Try it now → it's free</div>
              <div className="font-display text-lg">camp-compass.uk</div>
            </div>
            <div className="bg-white p-2 shrink-0">
              <QRCodeSVG value={PUBLIC_URL} size={84} bgColor="#ffffff" fgColor="#111111" includeMargin={false} />
            </div>
          </div>
        </Scene>

        {/* 5 — REAL CAMPERS (community / trust) */}
        <Scene index="5" bg={PHOTOS.community} gradient="linear-gradient(180deg,#8b5cf6 0%,#4c1d95 100%)">
          <div className="p-6 sm:p-8 pt-12">
            <StickerBadge><Users className="w-3.5 h-3.5" /> Real camper reviews</StickerBadge>
          </div>
          <div className="px-6 sm:px-8 text-center">
            <div className="font-display text-[36px] sm:text-[52px] leading-[1.02] mb-4 drop-shadow-lg">
              Real campers.<br/>Real opinions.<br/>No bots.
            </div>
            <div className="text-base sm:text-xl font-bold leading-snug text-white/90 mb-3">
              Crowdsourced prices. Star ratings. Birthday shout-outs.
            </div>
            <div className="text-sm text-white/75 max-w-[28ch] mx-auto leading-relaxed">
              Join thousands of UK campers sharing what's actually worth booking.
            </div>
          </div>
          <div className="p-6 sm:p-8 flex items-end justify-between gap-4">
            <div>
              <div className="text-[10px] mono uppercase tracking-[0.25em] text-white/80 mb-1">Join the community</div>
              <div className="font-display text-lg">camp-compass.uk</div>
            </div>
            <div className="bg-white p-2 shrink-0">
              <QRCodeSVG value={PUBLIC_URL} size={84} bgColor="#ffffff" fgColor="#111111" includeMargin={false} />
            </div>
          </div>
        </Scene>

        {/* 6 — SIGN UP NOW (final CTA) */}
        <Scene index="6" bg={PHOTOS.signup} gradient="linear-gradient(180deg,#111 0%,#000 100%)">
          <div className="p-6 sm:p-8 pt-12">
            <StickerBadge className="bg-white !text-black"><Compass className="w-3.5 h-3.5" /> Free while we grow</StickerBadge>
          </div>
          <div className="px-6 sm:px-8 text-center">
            <div className="font-display text-[44px] sm:text-[60px] leading-[0.95] mb-4 drop-shadow-lg">
              Sign up.<br/>Pack up.<br/>Drive out.
            </div>
            <div className="text-base sm:text-xl font-bold leading-snug text-[#ffb449]">
              Your next pitch is a tap away.
            </div>
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#ff4500] text-white font-bold uppercase tracking-wider text-xs">
              <Check className="w-3.5 h-3.5" /> Free account in 20 seconds <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="p-6 sm:p-8 flex items-end justify-between gap-4">
            <div>
              <div className="text-[10px] mono uppercase tracking-[0.25em] text-white/80 mb-1">Create account</div>
              <div className="font-display text-lg">camp-compass.uk</div>
            </div>
            <div className="bg-white p-2 shrink-0">
              <QRCodeSVG value={`${PUBLIC_URL}/register`} size={84} bgColor="#ffffff" fgColor="#111111" includeMargin={false} />
            </div>
          </div>
        </Scene>
      </div>

      {/* SCRIPT KIT — captions, hooks, hashtags, best times */}
      <section className="max-w-3xl mx-auto px-5 pb-20 space-y-8 text-white" data-testid="reel-scripts">
        <div className="border-l-2 border-[#ff4500] pl-4">
          <h2 className="font-display text-2xl mb-2">Caption + hashtag pack</h2>
          <p className="text-sm text-[#bbb]">
            Copy-paste straight into Instagram / TikTok / Facebook.
            Tip: use the <span className="text-[#ff4500] mono">{`{count}`}</span> token at the end as the LAST line of the caption — it makes the comment-bot click-through pop.
          </p>
        </div>

        {[
          {
            title: "1 · Bank Holiday Scorcher (HERO)",
            hook: "POV: it's bank holiday weekend and every pub garden is full.",
            caption: `POV: it's bank holiday weekend and every pub garden is full 🌞⛺\n\nMeanwhile, ${fmt} UK campsites are sitting empty.\n\nWe built CampCompass UK so you can find one in 60 seconds — no app store download, no faff. Just open & search.\n\nLink in bio 🔗 camp-compass.uk\n\n#bankholidayweekend #ukcamping #campingtrip #findacampsite`,
            hashtags: "#bankholidayweekend #ukcamping #campingtrip #findacampsite #campsiteuk #campervan #wildcamping #ukoutdoors #campingweekend #englishcountryside #greatoutdoors",
            best: "Thursday 5pm or Friday 8am — pre-weekend planning peak",
            sound: "Trending upbeat sound (sunny / chill / Britpop)",
          },
          {
            title: "2 · Scorching Weather",
            hook: "28°C and you're scrolling? Get outside.",
            caption: `28°C this weekend and you're scrolling? 🥵\n\nThe forecast says sun. The map says go. ⛺\n\nWe've mapped ${fmt} UK campsites so you can pick the right one in 60 seconds — search by price, dogs, hot showers, the lot.\n\nFree while we grow. 🔗 camp-compass.uk`,
            hashtags: "#heatwave #ukweather #campingholidays #campsite #sunnyweekend #escape #ukstaycation #camping #outdoorsuk #lastminutebooking",
            best: "Sunday 7pm — Monday-morning planning surge",
            sound: "Slow / cinematic golden-hour acoustic",
          },
          {
            title: "3 · 11,000+ Campsites (volume flex)",
            hook: "There are how many UK campsites?",
            caption: `Did you know there are over ${fmt} campsites in the UK? 🤯\n\nMost booking sites only show you the chains. We pulled in every farm site, glamping pod, mountain pitch and beach hideaway we could find — and put them all on one map.\n\nFilter by price, dogs, hot showers, kid-friendly, or wild & remote.\n\n🔗 camp-compass.uk`,
            hashtags: "#campinguk #campsite #ukstaycation #campervanlife #glamping #campingfamily #campingweekend #wildcamping #ukcampsites #travelhack",
            best: "Tuesday 7-9pm — high-intent evening browsing",
            sound: "Number-counter / pop-out trending sound",
          },
          {
            title: "4 · 60-Second Search (the why)",
            hook: "Stop opening 30 tabs to plan one weekend.",
            caption: `Stop opening 30 tabs to plan one weekend. 😩\n\nWe built one map. Every UK campsite. Real prices. Real reviews. One tap to book.\n\nThis took 8 months. It took you 60 seconds.\n\nFree while we grow. 🔗 camp-compass.uk`,
            hashtags: "#productivityhack #campinghacks #travelhack #weekendtrip #ukcamping #campervanlife #campinguk #campinglife #compareprices #stresslessholidays",
            best: "Monday 12-1pm or Wednesday 6pm",
            sound: "Quick-cut sound with claps or 'aha' moment",
          },
          {
            title: "5 · Real Campers, Real Opinions (community)",
            hook: "Don't trust the stars. Trust the campers.",
            caption: `Don't trust the stars. Trust the campers. 🏕️\n\nWe ditched fake review-bots. CampCompass UK runs on real camper reviews, crowdsourced prices, and a community forum where someone always answers within an hour.\n\nJoin thousands of UK campers swapping tips, photos and pitch warnings.\n\n🔗 camp-compass.uk`,
            hashtags: "#campingcommunity #ukcamping #campingforum #realreviews #campinglife #ukcampers #campsite #campinglove #thisiscamping #findyourpitch",
            best: "Saturday 11am — weekend trip-research peak",
            sound: "Warm storytelling / acoustic strum",
          },
          {
            title: "6 · Final CTA — Sign Up",
            hook: "Sign up. Pack up. Drive out.",
            caption: `Sign up. Pack up. Drive out. ✅\n\nYour next pitch is a tap away. Free account in 20 seconds — no app store download, no card needed.\n\n${fmt} UK campsites waiting.\n\n🔗 camp-compass.uk/register`,
            hashtags: "#signup #campingapp #ukcamping #campsite #campervanlife #campingfamily #freedom #escapethecity #greatoutdoors #weekendready",
            best: "Sunday 6pm — end-of-weekend FOMO conversion",
            sound: "Energetic / motivational fast cut",
          },
        ].map((s, i) => (
          <article key={i} className="p-5 border hairline bg-[#1a1a1a]" data-testid={`script-${i + 1}`}>
            <div className="text-[10px] mono uppercase tracking-[0.25em] text-[#ff4500] mb-2">{s.title}</div>
            <div className="font-display text-lg mb-3">{s.hook}</div>
            <div className="bg-[#0e0e0e] p-3 mb-3 whitespace-pre-wrap text-sm leading-relaxed">{s.caption}</div>
            <div className="grid sm:grid-cols-2 gap-3 text-xs">
              <div className="bg-[#0e0e0e] p-3">
                <div className="eyebrow text-[#888] mb-1">Hashtags</div>
                <div className="text-[#bbb] break-words leading-relaxed">{s.hashtags}</div>
              </div>
              <div className="space-y-2">
                <div className="bg-[#0e0e0e] p-3">
                  <div className="eyebrow text-[#888] mb-1">Best time to post</div>
                  <div className="text-white">{s.best}</div>
                </div>
                <div className="bg-[#0e0e0e] p-3">
                  <div className="eyebrow text-[#888] mb-1">Sound vibe</div>
                  <div className="text-white">{s.sound}</div>
                </div>
              </div>
            </div>
          </article>
        ))}

        <div className="p-5 border-2 border-[#ff4500] bg-[#1a1a1a]" data-testid="reel-pro-tips">
          <h3 className="font-display text-xl mb-2">3 Pro tips for the next 7 days</h3>
          <ul className="space-y-2 text-sm text-[#ddd] leading-relaxed list-decimal pl-5">
            <li><strong>Post scene 1 (Bank Holiday Scorcher) THURSDAY at 5pm</strong> — it's the strongest hook + bank-holiday Friday morning planning peak.</li>
            <li><strong>Reply to every comment within 1 hour.</strong> Instagram's algo rewards new accounts that have active comment sections in the first hour.</li>
            <li><strong>Pin your best-performing reel to the top of your profile.</strong> New profile visitors will see it first and that drives the most signups.</li>
          </ul>
        </div>
      </section>
    </div>
  );
}
